/**
 * HistorialVersiones — vista lado-a-lado de las versiones de una oferta (P2.2 FASE 3).
 *
 * Recibe oferta + onBack. Permite:
 *  - Ver todas las versiones en una columna izquierda con metadata (autor, fecha, balance).
 *  - Comparar dos versiones lado-a-lado (productos, parámetros, balances).
 *  - Ver feedback del cliente por versión (DEC-012).
 *  - Cambiar la versión activa.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, GitCompareArrows, Star, ChevronDown, ChevronRight, Building2, User } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("mobilsafe_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const eur = (n) => (Number.isFinite(n) ? n : 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

export default function HistorialVersiones({ oferta, onBack, onUpdated }) {
  const versiones = (oferta.versiones || []).slice().sort((a, b) => b.version - a.version);
  const [izq, setIzq] = useState(versiones[0]?.version);
  const [der, setDer] = useState(versiones[1]?.version || versiones[0]?.version);
  const [feedbackTxt, setFeedbackTxt] = useState("");
  const [savingFb, setSavingFb] = useState(false);

  const vIzq = versiones.find((v) => v.version === izq);
  const vDer = versiones.find((v) => v.version === der);

  async function activar(version) {
    try {
      await authAxios.put(`${API}/ofertas/${oferta.id}`, { version_activa: version });
      toast.success(`Versión ${version} activada`);
      onUpdated && onUpdated();
    } catch { toast.error("Error activando versión"); }
  }

  async function addFeedback(version) {
    if (!feedbackTxt.trim()) { toast.error("Escribe el feedback"); return; }
    setSavingFb(true);
    try {
      await authAxios.post(`${API}/ofertas/${oferta.id}/versiones/${version}/feedback`,
        { texto: feedbackTxt.trim() });
      setFeedbackTxt("");
      toast.success("Feedback añadido");
      onUpdated && onUpdated();
    } catch { toast.error("Error"); }
    setSavingFb(false);
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <button onClick={onBack} data-testid="historial-back" className="p-2 hover:bg-slate-100 rounded">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2"><GitCompareArrows className="w-5 h-5" /> Historial de versiones</h1>
            <p className="text-xs text-slate-500">{versiones.length} versiones · activa v{oferta.version_activa}</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-12 gap-4">
        {/* Lista de versiones */}
        <div className="col-span-3 bg-white rounded-xl border p-2 max-h-[80vh] overflow-y-auto">
          {versiones.map((v) => (
            <div key={v.version} className={`p-3 rounded-lg mb-1 cursor-pointer ${izq === v.version ? "bg-emerald-50 border border-emerald-200" : "hover:bg-slate-50"}`}
                 onClick={() => setIzq(v.version)} data-testid={`historial-v-${v.version}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-slate-900 text-sm flex items-center gap-1">
                  v{v.version}
                  {oferta.version_activa === v.version && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                </span>
                <button onClick={(e) => { e.stopPropagation(); setDer(v.version); }}
                  className="text-[10px] text-slate-500 hover:text-slate-900 underline">comparar →</button>
              </div>
              <p className="text-xs text-slate-500">{new Date(v.fecha_creacion).toLocaleDateString("es-ES")}</p>
              <p className="text-xs text-slate-500">{(v.escenarios || []).length} escenario(s)</p>
              {v.feedback_cliente?.length > 0 && (
                <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" /> {v.feedback_cliente.length} feedback(s)
                </p>
              )}
              {oferta.version_activa !== v.version && (
                <button onClick={(e) => { e.stopPropagation(); activar(v.version); }}
                  data-testid={`historial-activar-${v.version}`}
                  className="text-[10px] text-emerald-600 hover:underline mt-1">Activar</button>
              )}
            </div>
          ))}
        </div>

        {/* Comparación: tabla combinada con diff visual */}
        <div className="col-span-9 space-y-4">
          <DiffTabla vIzq={vIzq} vDer={vDer} />
          <div className="bg-white rounded-xl border p-4" data-testid="historial-feedback-panel">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Feedback del cliente · v{izq}</h3>
            <ul className="space-y-2 mb-3">
              {(vIzq?.feedback_cliente || []).map((f, i) => (
                <li key={i} className="border-l-2 border-amber-400 pl-3 py-1">
                  <p className="text-xs text-slate-500">{new Date(f.fecha).toLocaleString("es-ES")}</p>
                  <p className="text-sm text-slate-900">{f.texto}</p>
                </li>
              ))}
              {(vIzq?.feedback_cliente || []).length === 0 && <li className="text-sm italic text-slate-400">Sin feedback registrado.</li>}
            </ul>
            <div className="flex gap-2">
              <input value={feedbackTxt} onChange={(e) => setFeedbackTxt(e.target.value)} placeholder="Anota lo que dijo el cliente sobre esta versión..."
                data-testid="historial-feedback-input"
                className="flex-1 px-3 py-2 border rounded text-sm" />
              <button onClick={() => addFeedback(izq)} disabled={savingFb}
                data-testid="historial-feedback-add"
                className="text-sm px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                Añadir
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * IMPL-071 (2026-07-18) · Diff visual reemplazado por 2 acordeones de 5 KPIs cada uno:
 *   1. NEGOCIO MOBILSAFE (interno · navy): MRR SaaS · TCV · MB € y % · Payback interno · Múltiplo v/c
 *   2. ARGUMENTO CLIENTE (verde): Balance neto · Ahorro €/veh/mes · Payback cliente · CAES neto · Ahorro anual áreas
 * Cabecera colapsada muestra el KPI principal de cada bloque + su Δ%.
 * Cada fila lleva un comentario auto-generado según el signo/magnitud del cambio.
 */
const eurStr = (n) => (Number.isFinite(n) ? n : 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const num = (n) => Number.isFinite(n) ? n : 0;
const pct = (n, d) => {
  if (!d || !Number.isFinite(d) || d === 0) return null;
  return ((n - d) / Math.abs(d)) * 100;
};

// Extrae los 10 KPIs de una versión (usando escenario 0 · productos · resultados)
function extractKPIs(v) {
  if (!v) return null;
  const esc = (v.escenarios || [])[0] || {};
  const r = esc.resultados || {};
  const meses = v.parametros?.meses_analisis || 36;
  const totalVeh = (v.vehiculos || []).reduce((a, x) => a + (x.uds || 0), 0) || 1;

  // MOBILSAFE
  const mrr = num(r.mensual_saas_ms || r.cuota_saas_mes || 0);
  const totalHW = num(r.total_hw_venta || r.puntual_inversion || 0);
  const tcv = totalHW + mrr * meses;
  const mbEur = num(r.mb_total || r.margen_bruto_total || 0);
  const ventaTotal = num(r.venta_total_ms || tcv);
  const costeTotal = num(r.coste_total_ms || Math.max(ventaTotal - mbEur, 0));
  const mbPct = ventaTotal > 0 ? (mbEur / ventaTotal) * 100 : 0;
  const mbSaasMes = num(r.mb_saas_mes || (mbEur / meses));
  const paybackInterno = mbSaasMes > 0 ? totalHW / mbSaasMes : 0;
  const multiplo = costeTotal > 0 ? ventaTotal / costeTotal : 0;

  // CLIENTE
  const balanceNeto = num(r.periodo_balance || 0);
  const ahorroTotal = num(r.ahorro_operativo_total || r.ahorros_periodo || 0);
  const cuotasCliente = num(r.cuotas_cliente_periodo || (mrr * meses));
  const ahorroVehMes = totalVeh > 0 ? (balanceNeto / meses / totalVeh) : 0;
  const paybackCliente = num(r.meses_recuperacion || (totalHW && ahorroTotal ? (totalHW / (ahorroTotal / meses)) : 0));
  const caesNeto = num(r.caes_neto_cliente || 0);
  const ahorroAnual = ahorroTotal * (12 / meses);

  return {
    ms: { mrr, tcv, mbEur, mbPct, paybackInterno, multiplo },
    cl: { balanceNeto, ahorroVehMes, paybackCliente, caesNeto, ahorroAnual },
  };
}

// Genera comentario según delta absoluto y porcentual
function comentario(kind, vI, vD, higherIsBetter = true) {
  const d = vD - vI;
  const p = pct(vD, vI);
  const pStr = p == null ? "" : ` (${p >= 0 ? "+" : ""}${p.toFixed(1)}%)`;
  const mejor = higherIsBetter ? d > 0 : d < 0;
  const peor  = higherIsBetter ? d < 0 : d > 0;
  const igual = Math.abs(d) < 0.01;

  const templates = {
    mrr: {
      mejor: `Ingreso recurrente crece ${pStr} · más MRR blindado mes a mes`,
      peor:  `Ingreso recurrente baja ${pStr} · menos MRR blindado`,
      igual: `Ingreso recurrente estable · sin cambio en el compromiso mensual`,
    },
    tcv: {
      mejor: `Contrato vale más para MOBILSAFE ${pStr} · mejor valor total`,
      peor:  `Contrato vale menos ${pStr} · pérdida de valor total`,
      igual: `Valor del contrato sin cambios`,
    },
    mb: {
      mejor: `Rentabilidad interna mejora ${pStr} · %MB por encima objetivo 35%`,
      peor:  `Rentabilidad interna baja ${pStr} · vigilar %MB objetivo 35%`,
      igual: `Rentabilidad interna estable`,
    },
    payback_ms: {
      mejor: `Recuperamos coste HW más rápido · mejor cash-flow interno`,
      peor:  `Tardamos más en recuperar coste HW · peor cash-flow`,
      igual: `Payback interno sin cambios`,
    },
    multiplo: {
      mejor: `Mayor eficiencia · +€ ingresados por cada € de coste`,
      peor:  `Menor eficiencia · -€ ingresados por cada € de coste`,
      igual: `Ratio venta/coste estable`,
    },
    balance: {
      mejor: `El cliente termina con más € netos ${pStr} · argumento más potente`,
      peor:  `El cliente termina con menos € netos ${pStr}`,
      igual: `Balance neto sin cambios para el cliente`,
    },
    ahorro_vm: {
      mejor: `Cada vehículo ahorra más €/mes ${pStr} · frase de venta reforzada`,
      peor:  `Cada vehículo ahorra menos €/mes ${pStr}`,
      igual: `Ahorro por vehículo estable`,
    },
    payback_cli: {
      mejor: `El cliente recupera antes su inversión · más urgencia positiva`,
      peor:  `El cliente tarda más en recuperar · más objeción de precio`,
      igual: `Payback cliente sin cambios`,
    },
    caes: {
      mejor: `Ingreso puntual CAES año 1 mejora ${pStr} · más "regalo" al cliente`,
      peor:  `Ingreso puntual CAES año 1 baja ${pStr}`,
      igual: `CAES neto sin cambios`,
    },
    ahorro_anual: {
      mejor: `Ahorro operativo anual sube ${pStr} · narrativa de valor más fuerte`,
      peor:  `Ahorro operativo anual baja ${pStr}`,
      igual: `Ahorro anual estable`,
    },
  };
  const t = templates[kind] || templates.tcv;
  if (igual) return { txt: t.igual, tone: "same" };
  if (mejor) return { txt: t.mejor, tone: "better" };
  if (peor)  return { txt: t.peor, tone: "worse" };
  return { txt: t.igual, tone: "same" };
}

function DiffTabla({ vIzq, vDer }) {
  const [openMS, setOpenMS] = useState(false);
  const [openCL, setOpenCL] = useState(false);

  if (!vIzq || !vDer) {
    return <div className="bg-white rounded-xl border p-6 text-slate-400 text-sm">Selecciona dos versiones para comparar.</div>;
  }

  const kI = extractKPIs(vIzq);
  const kD = extractKPIs(vDer);
  const mismaVersion = vIzq.version === vDer.version;

  const filasMS = [
    { k: "mrr",         label: "MRR SaaS · €/mes",        vI: kI.ms.mrr,             vD: kD.ms.mrr,             fmt: eurStr,                                     hib: true  },
    { k: "tcv",         label: "TCV · valor del contrato", vI: kI.ms.tcv,             vD: kD.ms.tcv,             fmt: eurStr,                                     hib: true  },
    { k: "mb",          label: "Margen bruto · € / %",     vI: kI.ms.mbEur,           vD: kD.ms.mbEur,           fmt: (n, r) => `${eurStr(n)} / ${(r.ms.mbPct).toFixed(1)}%`, hib: true, sub: kI.ms.mbPct },
    { k: "payback_ms",  label: "Payback interno · meses",  vI: kI.ms.paybackInterno,  vD: kD.ms.paybackInterno,  fmt: (n) => `${n.toFixed(1)} m`,                 hib: false },
    { k: "multiplo",    label: "Múltiplo venta/coste",     vI: kI.ms.multiplo,        vD: kD.ms.multiplo,        fmt: (n) => `${n.toFixed(2)}×`,                  hib: true  },
  ];
  const filasCL = [
    { k: "balance",      label: "Balance neto periodo",    vI: kI.cl.balanceNeto,    vD: kD.cl.balanceNeto,    fmt: eurStr,                         hib: true  },
    { k: "ahorro_vm",    label: "Ahorro €/veh/mes",         vI: kI.cl.ahorroVehMes,   vD: kD.cl.ahorroVehMes,   fmt: (n) => `${eurStr(n)}/veh/m`,    hib: true  },
    { k: "payback_cli",  label: "Payback cliente · meses",  vI: kI.cl.paybackCliente, vD: kD.cl.paybackCliente, fmt: (n) => `${n.toFixed(1)} m`,     hib: false },
    { k: "caes",         label: "CAES neto Año 1",          vI: kI.cl.caesNeto,       vD: kD.cl.caesNeto,       fmt: eurStr,                         hib: true  },
    { k: "ahorro_anual", label: "Ahorro anual áreas",       vI: kI.cl.ahorroAnual,    vD: kD.cl.ahorroAnual,    fmt: eurStr,                         hib: true  },
  ];

  const principalMS = filasMS[0]; // MRR
  const principalCL = filasCL[0]; // Balance neto
  const dMS = pct(principalMS.vD, principalMS.vI);
  const dCL = pct(principalCL.vD, principalCL.vI);

  return (
    <div className="space-y-3" data-testid="historial-diff-tabla">
      {mismaVersion && (
        <div className="text-xs text-slate-500 italic px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
          Estás comparando la misma versión con ella misma. Selecciona 2 versiones distintas para ver diferencias.
        </div>
      )}

      {/* Acordeón 1 · NEGOCIO MOBILSAFE */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="diff-acordeon-ms">
        <button
          type="button"
          onClick={() => setOpenMS((v) => !v)}
          data-testid="diff-acordeon-ms-toggle"
          className="w-full flex items-center justify-between px-5 py-3 bg-[#1e3a5f] text-white hover:bg-[#152a44] transition"
        >
          <div className="flex items-center gap-3">
            {openMS ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <Building2 className="w-4 h-4" />
            <span className="font-bold uppercase tracking-wider text-sm">Negocio MOBILSAFE</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-white/70 text-xs">{principalMS.label.split(" · ")[0]}</span>
            <span className="font-bold tabular-nums">{principalMS.fmt(principalMS.vI, kI)}</span>
            <span className="text-white/50">→</span>
            <span className="font-bold tabular-nums">{principalMS.fmt(principalMS.vD, kD)}</span>
            {dMS != null && Math.abs(dMS) > 0.01 && (
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${dMS > 0 ? "bg-emerald-500 text-white" : "bg-rose-500 text-white"}`}>
                {dMS > 0 ? "+" : ""}{dMS.toFixed(1)}%
              </span>
            )}
          </div>
        </button>
        {openMS && (
          <FilasKPI filas={filasMS} kI={kI} kD={kD} testidPrefix="diff-ms" />
        )}
      </div>

      {/* Acordeón 2 · ARGUMENTO CLIENTE */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="diff-acordeon-cl">
        <button
          type="button"
          onClick={() => setOpenCL((v) => !v)}
          data-testid="diff-acordeon-cl-toggle"
          className="w-full flex items-center justify-between px-5 py-3 bg-[#0a6640] text-white hover:bg-[#075030] transition"
        >
          <div className="flex items-center gap-3">
            {openCL ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <User className="w-4 h-4" />
            <span className="font-bold uppercase tracking-wider text-sm">Argumento cliente</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-white/70 text-xs">{principalCL.label.split(" · ")[0]}</span>
            <span className="font-bold tabular-nums">{principalCL.fmt(principalCL.vI, kI)}</span>
            <span className="text-white/50">→</span>
            <span className="font-bold tabular-nums">{principalCL.fmt(principalCL.vD, kD)}</span>
            {dCL != null && Math.abs(dCL) > 0.01 && (
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${dCL > 0 ? "bg-emerald-500 text-white" : "bg-rose-500 text-white"}`}>
                {dCL > 0 ? "+" : ""}{dCL.toFixed(1)}%
              </span>
            )}
          </div>
        </button>
        {openCL && (
          <FilasKPI filas={filasCL} kI={kI} kD={kD} testidPrefix="diff-cl" />
        )}
      </div>
    </div>
  );
}

function FilasKPI({ filas, kI, kD, testidPrefix }) {
  return (
    <div className="divide-y divide-slate-100">
      {filas.map((f) => {
        const c = comentario(f.k, f.vI, f.vD, f.hib);
        const p = pct(f.vD, f.vI);
        const bg = c.tone === "better" ? "bg-emerald-50" : c.tone === "worse" ? "bg-rose-50" : "bg-white";
        return (
          <div key={f.k} className={`px-5 py-3 grid grid-cols-12 gap-4 items-center ${bg}`} data-testid={`${testidPrefix}-row-${f.k}`}>
            <div className="col-span-4">
              <p className="text-xs text-slate-500 uppercase font-semibold">{f.label}</p>
              <p className="text-sm text-slate-800 mt-0.5 tabular-nums">
                <span className="text-slate-600">{f.fmt(f.vI, kI)}</span>
                <span className="mx-2 text-slate-400">→</span>
                <span className="font-bold text-slate-900">{f.fmt(f.vD, kD)}</span>
              </p>
            </div>
            <div className="col-span-2 text-right">
              {p != null && Math.abs(p) > 0.01 && (
                <span className={`text-xs font-bold px-2 py-1 rounded ${c.tone === "better" ? "bg-emerald-500 text-white" : c.tone === "worse" ? "bg-rose-500 text-white" : "bg-slate-200 text-slate-700"}`}>
                  {p > 0 ? "+" : ""}{p.toFixed(1)}%
                </span>
              )}
            </div>
            <div className="col-span-6 text-sm text-slate-700 italic">{c.txt}</div>
          </div>
        );
      })}
    </div>
  );
}
